/**
 * Latin letters ↔ runes, in three alphabets: the Elder Futhark, the Younger Futhark (long-branch
 * or Danish variant) and the Anglo-Saxon Futhorc.
 *
 * This is transliteration, not translation. It spells the letters of a text with the runes of an
 * alphabet; it does not turn modern English into Old Norse or Old English, and it cannot
 * reproduce how any runic inscription was actually spelled.
 *
 * Sources:
 *
 * - The Unicode Standard, "Runic" block (U+16A0–U+16FF): the code points and their names,
 *   which separate the long-branch Younger Futhark forms (ᚬ ᚴ ᚼ ᛅ ᛋ ᛘ ᛦ) from the Elder
 *   Futhark forms (ᚨ ᚲ ᚺ ᛊ ᛗ ᛉ) and from the Anglo-Saxon ones (ᚩ ᚪ ᚫ ᚣ ᛡ ᛠ ᚳ ᛝ).
 * - Wikipedia, "Elder Futhark": the 24 runes with their standard transliteration (f u þ a r k
 *   g w / h n i j ï p z s / t b e m l ŋ d o).
 * - Wikipedia, "Younger Futhark": the 16 long-branch runes and the sounds each stands for (ᚢ for
 *   u, v, w, y, o, ø; ᚴ for k, g, ŋ; ᛁ for i, e; ᛅ for a, æ, e; ᛏ for t, d; ᛒ for b, p; ᛦ for ʀ).
 * - Wikipedia, "Anglo-Saxon runes": the 28-rune futhorc and its additions (ᚣ y, ᛡ io, ᛠ ea,
 *   ᚸ ḡ, ᛣ k, ᛢ q, ᛥ st) with their transliterations.
 *
 * What a program cannot do: the Younger Futhark has 16 runes for about 25 sounds, so spelling a
 * Latin text with it loses information that no reverse conversion can restore, and several Latin
 * letters have no rune in any of the three alphabets (they are spelled with the nearest rune
 * and listed in the table).
 */

export type RuneAlphabet = "elder" | "younger" | "futhorc";
export type ElderV = "wunjo" | "uruz" | "fehu";
export type YoungerO = "uruz" | "oss";
export type YoungerE = "isaz" | "ar";
export type WordSeparator = "space" | "dot";

export interface RuneOptions {
  /** Elder Futhark: which rune stands for v. */
  elderV: ElderV;
  /** Younger Futhark: which rune stands for o. */
  youngerO: YoungerO;
  /** Younger Futhark: which rune stands for e. */
  youngerE: YoungerE;
  /** Futhorc: spell ea and io with ᛠ and ᛡ. */
  futhorcDiphthongs: boolean;
  /** Put ᛫ between words instead of a space. */
  separator: WordSeparator;
}

export interface RuneResult {
  text: string;
  /** Letters (to runes) or runes (to Latin) that could not be converted, left unchanged. */
  unknown: string[];
}

const WORD_DOT = "᛫";
const LETTER = /\p{L}/u;
const COMBINING_MARKS = /[̀-ͯ]/g;
const SPACES = / /g;
const WORD_DOT_PATTERN = /᛫/g;

// ---------------------------------------------------------------------------------------
// Latin → runes
// ---------------------------------------------------------------------------------------

/** Letter-to-rune tables, written as "latin:runes" pairs. */
const ELDER: Record<string, string> = {
  a: "ᚨ",
  b: "ᛒ",
  c: "ᚲ",
  d: "ᛞ",
  e: "ᛖ",
  f: "ᚠ",
  g: "ᚷ",
  h: "ᚺ",
  i: "ᛁ",
  j: "ᛃ",
  k: "ᚲ",
  l: "ᛚ",
  m: "ᛗ",
  n: "ᚾ",
  o: "ᛟ",
  p: "ᛈ",
  q: "ᚲᚹ",
  r: "ᚱ",
  s: "ᛊ",
  t: "ᛏ",
  u: "ᚢ",
  v: "ᚹ",
  w: "ᚹ",
  x: "ᚲᛊ",
  y: "ᛃ",
  z: "ᛉ",
  þ: "ᚦ",
  ð: "ᚦ",
  th: "ᚦ",
  ŋ: "ᛜ",
  ng: "ᛜ",
  ï: "ᛇ",
  æ: "ᛇ",
  ø: "ᛟ",
  œ: "ᛟ",
};

const YOUNGER: Record<string, string> = {
  a: "ᛅ",
  æ: "ᛅ",
  b: "ᛒ",
  c: "ᚴ",
  d: "ᛏ",
  e: "ᛁ",
  f: "ᚠ",
  g: "ᚴ",
  h: "ᚼ",
  i: "ᛁ",
  j: "ᛁ",
  k: "ᚴ",
  l: "ᛚ",
  m: "ᛘ",
  n: "ᚾ",
  o: "ᚢ",
  ø: "ᚢ",
  p: "ᛒ",
  q: "ᚴᚢ",
  r: "ᚱ",
  ʀ: "ᛦ",
  s: "ᛋ",
  t: "ᛏ",
  u: "ᚢ",
  v: "ᚢ",
  w: "ᚢ",
  x: "ᚴᛋ",
  y: "ᚢ",
  z: "ᛋ",
  þ: "ᚦ",
  ð: "ᚦ",
  th: "ᚦ",
  ą: "ᚬ",
  ǫ: "ᚬ",
};

const FUTHORC: Record<string, string> = {
  a: "ᚪ",
  æ: "ᚫ",
  ae: "ᚫ",
  b: "ᛒ",
  c: "ᚳ",
  d: "ᛞ",
  e: "ᛖ",
  f: "ᚠ",
  g: "ᚷ",
  h: "ᚻ",
  i: "ᛁ",
  j: "ᛄ",
  k: "ᚳ",
  l: "ᛚ",
  m: "ᛗ",
  n: "ᚾ",
  o: "ᚩ",
  œ: "ᛟ",
  oe: "ᛟ",
  p: "ᛈ",
  q: "ᛢ",
  r: "ᚱ",
  s: "ᛋ",
  t: "ᛏ",
  u: "ᚢ",
  v: "ᚠ",
  w: "ᚹ",
  x: "ᛉ",
  y: "ᚣ",
  z: "ᛋ",
  þ: "ᚦ",
  ð: "ᚦ",
  th: "ᚦ",
  ŋ: "ᛝ",
  ng: "ᛝ",
  ï: "ᛇ",
};

const FUTHORC_DIPHTHONGS: Record<string, string> = { ea: "ᛠ", io: "ᛡ" };

const TABLES: Record<RuneAlphabet, Record<string, string>> = {
  elder: ELDER,
  younger: YOUNGER,
  futhorc: FUTHORC,
};

const SPECIAL = new Set([..."þðŋæœøïʀąǫ"]);

function buildTable(
  alphabet: RuneAlphabet,
  options: RuneOptions
): Record<string, string> {
  const table: Record<string, string> = { ...TABLES[alphabet] };
  if (alphabet === "elder") {
    table.v = { wunjo: "ᚹ", uruz: "ᚢ", fehu: "ᚠ" }[options.elderV];
  } else if (alphabet === "younger") {
    table.o = options.youngerO === "oss" ? "ᚬ" : "ᚢ";
    table.e = options.youngerE === "ar" ? "ᛅ" : "ᛁ";
  } else if (options.futhorcDiphthongs) {
    Object.assign(table, FUTHORC_DIPHTHONGS);
  }
  return table;
}

function foldLetter(ch: string): string {
  if (SPECIAL.has(ch)) {
    return ch;
  }
  const base = ch.normalize("NFD").replace(COMBINING_MARKS, "");
  return base || ch;
}

function matchRune(
  chars: readonly string[],
  at: number,
  table: Readonly<Record<string, string>>
): { runes: string; length: number } | undefined {
  for (let length = Math.min(2, chars.length - at); length > 0; length -= 1) {
    const key = chars.slice(at, at + length).join("");
    const runes = table[key];
    if (runes) {
      return { runes, length };
    }
  }
  return;
}

function separate(text: string, options: RuneOptions): string {
  return options.separator === "dot" ? text.replace(SPACES, WORD_DOT) : text;
}

/** Spell Latin text with the runes of an alphabet; other characters pass through. */
export function toRunes(
  input: string,
  alphabet: RuneAlphabet,
  options: RuneOptions
): RuneResult {
  const table = buildTable(alphabet, options);
  const chars = [...input.normalize("NFC").toLowerCase()].map(foldLetter);
  const unknown = new Set<string>();
  let out = "";
  let i = 0;
  while (i < chars.length) {
    const ch = chars[i] as string;
    const hit = matchRune(chars, i, table);
    if (hit) {
      out += hit.runes;
      i += hit.length;
      continue;
    }
    if (LETTER.test(ch)) {
      unknown.add(ch);
    }
    out += ch;
    i += 1;
  }
  return { text: separate(out, options), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Runes → Latin
// ---------------------------------------------------------------------------------------

/** The conventional transliteration of each rune, per alphabet. */
const READ: Record<RuneAlphabet, Record<string, string>> = {
  elder: {
    ᚠ: "f",
    ᚢ: "u",
    ᚦ: "þ",
    ᚨ: "a",
    ᚱ: "r",
    ᚲ: "k",
    ᚷ: "g",
    ᚹ: "w",
    ᚺ: "h",
    ᚾ: "n",
    ᛁ: "i",
    ᛃ: "j",
    ᛇ: "ï",
    ᛈ: "p",
    ᛉ: "z",
    ᛊ: "s",
    ᛏ: "t",
    ᛒ: "b",
    ᛖ: "e",
    ᛗ: "m",
    ᛚ: "l",
    ᛜ: "ŋ",
    ᛞ: "d",
    ᛟ: "o",
  },
  younger: {
    ᚠ: "f",
    ᚢ: "u",
    ᚦ: "þ",
    ᚬ: "ą",
    ᚱ: "r",
    ᚴ: "k",
    ᚼ: "h",
    ᚽ: "h",
    ᚾ: "n",
    ᚿ: "n",
    ᛁ: "i",
    ᛅ: "a",
    ᛆ: "a",
    ᛋ: "s",
    ᛌ: "s",
    ᛏ: "t",
    ᛐ: "t",
    ᛒ: "b",
    ᛓ: "b",
    ᛘ: "m",
    ᛙ: "m",
    ᛚ: "l",
    ᛦ: "ʀ",
    ᛧ: "ʀ",
  },
  futhorc: {
    ᚠ: "f",
    ᚢ: "u",
    ᚦ: "þ",
    ᚩ: "o",
    ᚱ: "r",
    ᚳ: "c",
    ᚷ: "g",
    ᚹ: "w",
    ᚻ: "h",
    ᚺ: "h",
    ᚾ: "n",
    ᛁ: "i",
    ᛄ: "j",
    ᛇ: "ï",
    ᛈ: "p",
    ᛉ: "x",
    ᛋ: "s",
    ᛏ: "t",
    ᛒ: "b",
    ᛖ: "e",
    ᛗ: "m",
    ᛚ: "l",
    ᛝ: "ŋ",
    ᛟ: "œ",
    ᛞ: "d",
    ᚪ: "a",
    ᚫ: "æ",
    ᚣ: "y",
    ᛡ: "io",
    ᛠ: "ea",
    ᚸ: "ḡ",
    ᛣ: "k",
    ᛢ: "q",
    ᛥ: "st",
  },
};

/** Read runes back as Latin letters; the word dot becomes a space. */
export function fromRunes(input: string, alphabet: RuneAlphabet): RuneResult {
  const table = READ[alphabet];
  const unknown = new Set<string>();
  let out = "";
  for (const ch of input.replace(WORD_DOT_PATTERN, " ")) {
    const value = table[ch];
    if (value !== undefined) {
      out += value;
    } else {
      if (isRune(ch)) {
        unknown.add(ch);
      }
      out += ch;
    }
  }
  return { text: out, unknown: [...unknown] };
}

function isRune(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  return code >= 0x16_a0 && code <= 0x16_f8 && code !== 0x16_eb;
}

// ---------------------------------------------------------------------------------------
// Rune table
// ---------------------------------------------------------------------------------------

export interface RuneRow {
  rune: string;
  /** Standard transliteration of the rune. */
  letter: string;
  /** Rune name, from the Unicode character name. */
  name: string;
}

const NAMES: Record<RuneAlphabet, Record<string, string>> = {
  elder: {
    ᚠ: "fehu",
    ᚢ: "uruz",
    ᚦ: "thurisaz",
    ᚨ: "ansuz",
    ᚱ: "raido",
    ᚲ: "kauna",
    ᚷ: "gebo",
    ᚹ: "wunjo",
    ᚺ: "haglaz",
    ᚾ: "naudiz",
    ᛁ: "isaz",
    ᛃ: "jeran",
    ᛇ: "iwaz",
    ᛈ: "pertho",
    ᛉ: "algiz",
    ᛊ: "sowilo",
    ᛏ: "tiwaz",
    ᛒ: "berkanan",
    ᛖ: "ehwaz",
    ᛗ: "mannaz",
    ᛚ: "laukaz",
    ᛜ: "ingwaz",
    ᛞ: "dagaz",
    ᛟ: "othalan",
  },
  younger: {
    ᚠ: "fé",
    ᚢ: "úr",
    ᚦ: "þurs",
    ᚬ: "óss",
    ᚱ: "reið",
    ᚴ: "kaun",
    ᚼ: "hagall",
    ᚾ: "nauðr",
    ᛁ: "íss",
    ᛅ: "ár",
    ᛋ: "sól",
    ᛏ: "týr",
    ᛒ: "bjarkan",
    ᛘ: "maðr",
    ᛚ: "lögr",
    ᛦ: "ýr",
  },
  futhorc: {
    ᚠ: "feoh",
    ᚢ: "ur",
    ᚦ: "thorn",
    ᚩ: "os",
    ᚱ: "rad",
    ᚳ: "cen",
    ᚷ: "gyfu",
    ᚹ: "wynn",
    ᚻ: "haegl",
    ᚾ: "nyd",
    ᛁ: "is",
    ᛄ: "ger",
    ᛇ: "eoh",
    ᛈ: "peorth",
    ᛉ: "eolhx",
    ᛋ: "sigel",
    ᛏ: "tir",
    ᛒ: "beorc",
    ᛖ: "eh",
    ᛗ: "man",
    ᛚ: "lagu",
    ᛝ: "ing",
    ᛟ: "ethel",
    ᛞ: "daeg",
    ᚪ: "ac",
    ᚫ: "aesc",
    ᚣ: "yr",
    ᛡ: "ior",
    ᛠ: "ear",
    ᚸ: "gar",
    ᛣ: "calc",
    ᛢ: "cweorth",
    ᛥ: "stan",
  },
};

/** The rune table for an alphabet, in the order the alphabet is learned. */
export function runeRows(alphabet: RuneAlphabet): RuneRow[] {
  const names = NAMES[alphabet];
  const read = READ[alphabet];
  return Object.keys(names).map((rune) => ({
    rune,
    letter: read[rune] ?? "",
    name: names[rune] ?? "",
  }));
}

export const RUNE_ALPHABETS: readonly RuneAlphabet[] = [
  "elder",
  "younger",
  "futhorc",
];
